import React, { useEffect, useState } from "react";
import { api, fmtError, fmtDate } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash, Stack } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";

const METRIC_TYPES = [
    { value: "number", label: "Number" },
    { value: "percent", label: "Percent (%)" },
    { value: "text", label: "Text" },
];

const COLORS = [
    "#0033A0", "#E0301E", "#15803D", "#B45309", "#7E22CE",
    "#0F766E", "#DB2777", "#1E40AF", "#65A30D", "#475569",
];

export default function Platforms() {
    const [data, setData] = useState({ builtin: [], custom: [] });
    const [loading, setLoading] = useState(true);
    const [openAdd, setOpenAdd] = useState(false);
    const confirm = useConfirm();

    const load = async () => {
        try {
            const { data } = await api.get("/platforms");
            setData(data);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        const p = (data.custom || []).find((x) => x.id === id);
        const ok = await confirm({
            title: "Remove this platform?",
            description: p?.name
                ? `"${p.name}" will no longer appear in weekly report templates. Built-ins are unaffected.`
                : "This custom platform will no longer appear in weekly report templates.",
            confirmText: "Remove platform",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/platforms/${id}`);
            toast.success("Platform removed");
            load();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// Catalog</div>
                    <h1 className="font-display text-4xl tracking-tighter">Platforms</h1>
                    <p className="text-slate-500 mt-2 text-sm">
                        Built-in platforms come with default metric templates. Add your own platforms with custom metric fields so weekly reports stay consistent.
                    </p>
                </div>
                <Button
                    onClick={() => setOpenAdd(true)}
                    data-testid="add-platform-button"
                    className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2"
                >
                    <Plus size={14} weight="bold" /> Add platform
                </Button>
            </header>

            {loading && <p className="text-xs font-mono text-slate-400">loading…</p>}

            {!loading && (
                <>
                    <section>
                        <div className="overline mb-3">Built-in · {data.builtin.length}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="platforms-builtin">
                            {data.builtin.map((p) => (
                                <PlatformCard key={p.name} platform={p} builtin />
                            ))}
                        </div>
                    </section>

                    <section>
                        <div className="overline mb-3">Custom · {data.custom.length}</div>
                        {data.custom.length === 0 ? (
                            <div className="border border-dashed border-slate-300 px-6 py-10 text-center" data-testid="platforms-custom-empty">
                                <Stack size={28} className="text-slate-400 mx-auto" />
                                <p className="text-sm text-slate-500 mt-2">No custom platforms yet. Click &ldquo;Add platform&rdquo; to define one.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="platforms-custom">
                                {data.custom.map((p) => (
                                    <PlatformCard key={p.id} platform={p} onRemove={() => remove(p.id)} />
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}

            {openAdd && (
                <AddPlatformModal
                    onClose={() => setOpenAdd(false)}
                    onAdded={() => { setOpenAdd(false); load(); }}
                />
            )}
        </div>
    );
}

function PlatformCard({ platform, builtin = false, onRemove }) {
    return (
        <div className="bg-white p-5" data-testid={`platform-card-${platform.name || platform.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="w-3 h-3 mt-1.5 shrink-0" style={{ backgroundColor: platform.color || "#0033A0" }} />
                    <div className="min-w-0">
                        <div className="font-display text-lg truncate">{platform.label || platform.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{platform.name}</div>
                    </div>
                </div>
                {builtin ? (
                    <span className="font-mono text-[10px] uppercase tracking-wider bg-slate-900 text-white px-2 py-0.5 whitespace-nowrap">built-in</span>
                ) : (
                    <button
                        onClick={onRemove}
                        data-testid={`platform-remove-${platform.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-700"
                        title="Remove"
                    >
                        <Trash size={14} />
                    </button>
                )}
            </div>
            <div className="border-t border-slate-100 pt-3">
                <div className="overline mb-1" style={{ fontSize: 9 }}>Metrics · {(platform.metrics || []).length}</div>
                {(platform.metrics || []).length === 0 ? (
                    <p className="text-xs text-slate-500">No metric fields.</p>
                ) : (
                    <ul className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin">
                        {platform.metrics.map((m) => (
                            <li key={m.key} className="flex items-center justify-between gap-2 text-xs">
                                <span className="truncate">
                                    {m.label}
                                    {m.unit && <span className="text-slate-400 ml-1">({m.unit})</span>}
                                </span>
                                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 shrink-0">{m.type}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {platform.created_at && (
                <div className="overline text-slate-400 mt-3" style={{ fontSize: 9 }}>added {fmtDate(platform.created_at)}</div>
            )}
        </div>
    );
}

function AddPlatformModal({ onClose, onAdded }) {
    const [form, setForm] = useState({
        name: "",
        label: "",
        color: COLORS[0],
        metrics: [
            { key: "impressions", label: "Impressions", type: "number" },
            { key: "clicks", label: "Clicks", type: "number" },
            { key: "spend", label: "Spend", type: "number", unit: "$" },
        ],
    });
    const [busy, setBusy] = useState(false);

    const addRow = () => setForm((f) => ({ ...f, metrics: [...f.metrics, { key: "", label: "", type: "number" }] }));
    const removeRow = (i) => setForm((f) => ({ ...f, metrics: f.metrics.filter((_, idx) => idx !== i) }));
    const updateRow = (i, patch) => {
        const next = [...form.metrics];
        next[i] = { ...next[i], ...patch };
        setForm({ ...form, metrics: next });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Platform name is required.");
            return;
        }
        if (form.metrics.length === 0) {
            toast.error("Add at least one metric field.");
            return;
        }
        // Auto-generate keys from labels client-side too
        const metrics = form.metrics
            .filter((m) => m.label && m.label.trim())
            .map((m) => ({
                key: (m.key || m.label).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "metric",
                label: m.label.trim(),
                type: m.type || "number",
                unit: m.unit?.trim() || null,
            }));
        setBusy(true);
        try {
            await api.post("/platforms", {
                name: form.name.trim(),
                label: form.label.trim() || null,
                color: form.color,
                metrics,
            });
            toast.success("Platform added");
            onAdded?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="platform-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-2xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Catalog</div>
                        <h3 className="font-display text-lg">Add platform</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="platform-modal-close">×</button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Platform name (used as ID)">
                            <Input
                                data-testid="platform-name"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Reddit Ads, Spotify, Hotstar"
                                className="rounded-none border-slate-300"
                            />
                        </Field>
                        <Field label="Display label (optional)">
                            <Input
                                data-testid="platform-label"
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="Reddit Ads · Self-serve"
                                className="rounded-none border-slate-300"
                            />
                        </Field>
                    </div>

                    <div>
                        <Label className="overline mb-2 block">Brand color</Label>
                        <div className="flex flex-wrap gap-2" data-testid="platform-color">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setForm({ ...form, color: c })}
                                    data-testid={`platform-color-${c.replace("#", "")}`}
                                    className={`w-7 h-7 ${form.color === c ? "ring-2 ring-offset-2 ring-slate-900" : ""}`}
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="overline">Metric fields</Label>
                            <button
                                type="button"
                                onClick={addRow}
                                data-testid="platform-metric-add"
                                className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline inline-flex items-center gap-1"
                            >
                                <Plus size={12} /> Add metric
                            </button>
                        </div>
                        <div className="border border-slate-200">
                            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
                                <div className="col-span-5 overline" style={{ fontSize: 9 }}>Label</div>
                                <div className="col-span-3 overline" style={{ fontSize: 9 }}>Type</div>
                                <div className="col-span-3 overline" style={{ fontSize: 9 }}>Unit</div>
                                <div className="col-span-1" />
                            </div>
                            {form.metrics.map((m, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 items-center" data-testid={`platform-metric-row-${i}`}>
                                    <div className="col-span-5">
                                        <Input
                                            value={m.label}
                                            onChange={(e) => updateRow(i, { label: e.target.value })}
                                            placeholder="Impressions"
                                            className="rounded-none border-slate-300 h-9"
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <Select value={m.type} onValueChange={(v) => updateRow(i, { type: v })}>
                                            <SelectTrigger className="rounded-none border-slate-300 h-9"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {METRIC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-3">
                                        <Input
                                            value={m.unit || ""}
                                            onChange={(e) => updateRow(i, { unit: e.target.value })}
                                            placeholder="$ / s / —"
                                            className="rounded-none border-slate-300 h-9 font-mono"
                                        />
                                    </div>
                                    <div className="col-span-1 text-right">
                                        <button
                                            type="button"
                                            onClick={() => removeRow(i)}
                                            data-testid={`platform-metric-remove-${i}`}
                                            className="p-1.5 text-slate-400 hover:text-red-700"
                                        >
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {form.metrics.length === 0 && (
                                <div className="px-3 py-4 text-xs text-slate-500 text-center">No metrics yet — click &ldquo;Add metric&rdquo;.</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="platform-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Saving…" : "Add platform"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
